import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { Link } from 'react-router-dom';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TextOverflowTooltip, TableSkeleton } from 'src/common';
import { ComplianceFrameworkQuery, ControlsQuery } from 'src/graphql/_generated';
import GenericTable from 'src/components/GenericTable';
import { StatusIndicator } from './StatusIndicator';
import { ControllStatus } from 'src/api/types';

type Control =
  ComplianceFrameworkQuery['complianceFrameworks'][number]['complianceControls'][number]['compliantRmpControls'][number];

interface ExpandContentProps {
  controls: Control[];
}

const defaultColumns = ['source_framework', 'name', 'status', 'owner', 'strategy'];
const internalIds = [GTInternalIds.tdm];

export const ExpandContent: React.FC<ExpandContentProps> = ({ controls }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.rmpFromFramework}-columns`, defaultColumns);

  const columns = useMemo(() => {
    const list: ColumnDef<Control>[] = [
      {
        id: 'source_framework',
        header: 'Source Framework',
        accessorFn: (row) => {
          const framework = row.frameworkControls[0] || null;

          if (!framework.controlFramework) return null;
          return (
            <Link className="table-link" to={`/control-frameworks/${framework.controlFramework.id}`}>
              {framework.controlFramework.shortname}
            </Link>
          );
        },
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              to={`/risk-management/control/${info.row.original.id}`}
              className="table-link"
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['Framework'],
                  },
                  destination: { value: ['Control'], params: { id: info.row.original.id } },
                  tags: ['RMP', 'FRAMEWORK'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: (info) => <StatusIndicator status={info.getValue() as ControllStatus} />,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => row.owner?.text,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.strategy || null,
        cell: (info) => info.getValue(),
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: () => {
          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [selectedColumns]);

  if (!controls) return <TableSkeleton />;

  return (
    <GenericTable
      tableId={TableIds.rmpFromFramework}
      data={controls}
      columns={columns}
      GTColumnSelectAnchorExported={
        <GTColumnSelectAnchor
          tableId={TableIds.rmpFromFramework}
          options={[
            { key: 'source_framework', title: 'Source Framework' },
            { key: 'name', title: 'Name' },
            { key: 'status', title: 'Status' },
            { key: 'owner', title: 'Owner' },
            { key: 'strategy', title: 'Strategy' },
          ]}
          defaultOptions={defaultColumns}
          value={selectedColumns}
          onChange={setSelectedColumns}
        />
      }
      darkMode
    />
  );
};
